<template>
	<Modal width="small" @close="emitCloseModal" @submit="saveUser" :texts="{ headerPrimary: modalTitle }">
		<section class="form-container">
			<div class="task-main-info">
				<template v-for="(input, idx) in modalInputs">
					<div class="input-container" :key="input.keyInUser + idx">
						<label>
							{{ $t(input.label) }}
							<Input
								class="modal-input"
								dir="auto"
								@click.right.prevent
								:class="input.keyInUser + '-input'"
								:placeholder="$t(input.label).toLowerCase()"
								:type="input.keyInUser === 'password' ? 'password' : 'text'"
								v-model.trim="user[input.keyInUser]"
								:flash="isErrorShown(input.label)"
							/>
						</label>
					</div>
				</template>
			</div>
			<div class="task-secondary-info">
				<div class="input-container">
					<div>{{ $t('type') }}</div>
					<Select class="select-container" v-model="user.type">
						<Option v-for="(type, idx) in userTypes" :key="type + idx" :label="$t(type)" :value="type" />
					</Select>
					<ErrorMsg :isShown="isErrorShown('type')" :text="`${$t('please_select')} ${$t('type')}`" />
				</div>
				<div class="input-container">
					<div>{{ $t('level') }}</div>
					<Select class="select-container" v-model="user.level">
						<Option v-for="(level, idx) in userLevels" :key="level + idx" :label="$t(level)" :value="level" />
					</Select>
					<ErrorMsg :isShown="isErrorShown('level')" :text="`${$t('please_select')} ${$t('level')}`" />
				</div>
				<div class="input-container">
					<div>{{ $t('job_function') }}</div>
					<Select class="select-container" v-model="user.jobFunction">
						<Option
							v-for="(jobFunction, idx) in jobFunctions"
							:key="jobFunction + idx"
							:label="$t(jobFunction)"
							:value="jobFunction"
						/>
					</Select>
				</div>
				<div class="input-container">
					<div>{{ $t('language') }}</div>
					<Select class="select-container" v-model="user.language">
						<Option
							v-for="(lang, idx) in languages"
							:key="lang.label + idx"
							:label="$t(lang.label)"
							:value="lang.value"
							:src="lang.logo_url"
						/>
					</Select>
					<ErrorMsg :isShown="isErrorShown('invalid_language')" :text="`${$t('invalid_language')}`" />
				</div>
				<div class="input-container">
					<div>{{ $t('panel_permitted') }}</div>
					<Select class="select-container" v-model="user.panelViews" :multiple="true">
						<Option
							v-for="(option, idx) in panelPermittedOptions"
							:key="option + idx"
							:label="$t(option)"
							:value="option"
						/>
					</Select>
					<ErrorMsg
						:isShown="isErrorShown('panel_permitted')"
						:text="`${$t('please_select')} ${$t('panel_permitted')}`"
					/>
				</div>
				<div v-if="isSuperVisor">
					<div>{{ $t('working_areas') }}</div>
					<Select class="select-container" v-model="user.workingAreaIds" :multiple="true">
						<SearchBar v-model="workingAreaSearch" />
						<Option
							v-for="(workingArea, idx) in filteredWorkingAreas"
							:key="workingArea.id + idx"
							:label="workingArea.area_name"
							:value="workingArea.id"
						/>
					</Select>
				</div>
			</div>
		</section>
	</Modal>
</template>

<script>
import Input from '@/modules/common/components/Input'
import Modal from '@/modules/common/components/Modal'
import Option from '@/modules/common/components/Option'
import Select from '@/modules/common/components/Select'
import ErrorMsg from '@/modules/common/components/ErrorMsg'
import SearchBar from '@/modules/common/components/SearchBar'
import authService from '../../auth/services/authService'
import { REAL_TIME_CLEANING_PERMISSION, REAL_TIME_MAINTENANCE_PERMISSION } from '@/modules/common/consts/consts'

export default {
	name: 'UserModal',
	components: { Modal, ErrorMsg, Option, Select, Input, SearchBar },
	props: {
		userProp: { type: Object, required: true },
		languages: { type: Array, required: true },
		workingAreas: { type: Array, required: true }
	},
	data() {
		return {
			errors: [],
			workingAreaSearch: '',
			user: JSON.parse(JSON.stringify(this.userProp)),
			modalInputs: [
				{
					label: 'first_name',
					keyInUser: 'firstName'
				},
				{
					label: 'last_name',
					keyInUser: 'lastName'
				},
				{
					label: 'username',
					keyInUser: 'username'
				},
				{
					label: 'password',
					keyInUser: 'password'
				},
				{
					label: 'email',
					keyInUser: 'email'
				},
				{
					label: 'phone',
					keyInUser: 'phone'
				}
			],
			userTypes: ['viewer', 'installer'],
			userLevels: ['viewer', 'supervisor'],
			jobFunctions: [
				'property_manager',
				'facility_manager',
				'site_manager',
				'workplace_manager',
				'cleaning_company_ceo',
				'operations_manager',
				'area_manager',
				'site_cleaning_manager',
				'cleaning_supervisor',
				'other'
			],
			panelPermittedOptions: [
				'dashboard',
				REAL_TIME_CLEANING_PERMISSION,
				REAL_TIME_MAINTENANCE_PERMISSION,
				'pad-presence-testing',
				'survey',
				'room',
				'task',
				'users',
				'devices',
				'working_area'
			],
			originalFields: {
				email: this.userProp.email || '',
				username: this.userProp.username || '',
				phone: this.userProp.phone || ''
			}
		}
	},
	methods: {
		emitCloseModal() {
			this.$emit('closeModal')
		},
		async checkForm() {
			this.errors = []

			const restrictedUsernames = ['admin', 'viewer', 'installer', 'supervisor']
			const {
				id,
				firstName,
				lastName,
				username,
				password,
				email,
				phone,
				type,
				level,
				language,
				panelViews,
				workingAreaIds
			} = this.user

			const { email: originalEmail, username: originalUsername, phone: originalPhone } = this.originalFields

			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			const usernameRegex = /^(?=.*[a-zA-Z])[a-zA-Z0-9]+$/g
			const phoneRegex = /^\d+$/

			// Helper to check and add error
			const addErrorIfFalsy = (condition, errorField) => {
				if (!condition) this.addError(errorField)
			}

			addErrorIfFalsy(firstName, 'first_name')
			addErrorIfFalsy(lastName, 'last_name')
			addErrorIfFalsy(username && !restrictedUsernames.includes(username) && usernameRegex.test(username), 'username')
			addErrorIfFalsy(password || id, 'password')
			addErrorIfFalsy(email && emailRegex.test(email), 'email')
			addErrorIfFalsy(level, 'level')
			addErrorIfFalsy(phone && phoneRegex.test(phone) && phone.length >= 10, 'phone')
			addErrorIfFalsy(type, 'type')
			addErrorIfFalsy(language, 'invalid_language')
			addErrorIfFalsy(panelViews && panelViews.length > 0, 'panel_permitted')

			if (!this.isSuperVisor && workingAreaIds.length > 0) {
				this.user.workingAreaIds = []
			}

			const validations = {
				isEmailValidPrm: (!id || email !== originalEmail) && email ? authService.isEmailValid(email) : true,
				isUsernameValidPrm:
					(!id || username !== originalUsername) && username ? authService.isUsernameValid(username) : true,
				isPhoneValidPrm: (!id || phone !== originalPhone) && phone ? authService.isPhoneValid(phone) : true
			}

			try {
				const [isEmailValid, isUsernameValid, isPhoneValid] = await Promise.all(Object.values(validations))

				if (!isEmailValid) this.addError('email')
				if (!isUsernameValid) this.addError('username')
				if (!isPhoneValid) this.addError('phone')
			} catch (error) {
				// Handle error
			}
		},
		addError(error) {
			this.errors.push(error)
		},
		isErrorShown(error) {
			return this.errors.includes(error)
		},
		async saveUser() {
			await this.checkForm()
			if (this.errors.length > 0) return
			this.$emit('save', this.user)
		}
	},
	computed: {
		modalTitle() {
			return this.user.id ? this.$t('edit_user') : this.$t('new_user')
		},
		langFlag() {
			const { language: lang } = this.user
			return this.languages.find((language) => language.value === lang)?.logo_url
		},
		isSuperVisor() {
			return this.user.level === 'supervisor'
		},
		filteredWorkingAreas() {
			const areas = this.workingAreas || []
			const query = String(this.workingAreaSearch || '').trim().toLowerCase()
			if (!query) return areas
			return areas.filter((area) => String(area.area_name || '').toLowerCase().includes(query))
		}
	}
}
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

.form-container {
	.task-main-info,
	.task-secondary-info {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
	}
}

.input-container {
	margin-bottom: 15px;
	font-size: 14px;
	label {
		color: #333;
		& > * {
			border: 1px solid $grey-border-color;
			border-radius: 4px;
			padding: 8px;
			margin: 0;
		}
	}
}

.md-field.select-container {
	width: 100% !important;
}

.select-container {
	border-bottom: 1px solid $grey-border-color;
}
</style>
